import { supabase } from '../lib/supabase/client';
import { Database } from '../types/database.types';
import { ConsultationBooking, ConsultationBookingInput } from '../types/models';
import { emailService } from './emailService';

type BookingRow = Database['public']['Tables']['consultation_bookings']['Row'];
type BookingInsert = Database['public']['Tables']['consultation_bookings']['Insert'];

interface BookingResponse {
  data: ConsultationBooking | null;
  error: Error | null;
}

export const consultationService = {
  async createBooking(input: ConsultationBookingInput): Promise<BookingResponse> {
    try {
      // Validate required fields
      if (!input.fullName?.trim()) {
        return {
          data: null,
          error: new Error('Full name is required'),
        };
      }

      if (!input.email?.trim()) {
        return {
          data: null,
          error: new Error('Email is required'),
        };
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input.email)) {
        return {
          data: null,
          error: new Error('Please enter a valid email address'),
        };
      }

      // Convert camelCase to snake_case for DB
      const insertData: BookingInsert = {
        full_name: input.fullName.trim(),
        email: input.email.toLowerCase().trim(),
        phone: input.phone?.trim() || null,
        company_name: input.companyName?.trim() || null,
        message: input.message?.trim() || null,
        booking_type: input.bookingType,
        status: 'pending',
      };

      const { data, error } = await supabase
        .from('consultation_bookings')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Convert snake_case to camelCase for app
      const booking: ConsultationBooking = {
        id: data.id,
        fullName: data.full_name,
        email: data.email,
        phone: data.phone || undefined,
        companyName: data.company_name || undefined,
        message: data.message || undefined,
        bookingType: data.booking_type,
        createdAt: data.created_at,
        status: data.status,
      };

      // Send confirmation and follow-up emails (don't block on email sending)
      const emailData = {
        fullName: data.full_name,
        email: data.email,
        phone: data.phone || undefined,
        companyName: data.company_name || undefined,
        message: data.message || undefined,
        bookingType: data.booking_type,
        bookingId: data.id,
      };

      // Send confirmation email to customer
      emailService.sendConsultationConfirmation(data.email, emailData).catch((err) => {
        console.error('Customer email send error:', err);
      });

      // Send follow-up email to team
      emailService.sendConsultationFollowup(emailData).catch((err) => {
        console.error('Team email send error:', err);
      });

      return { data: booking, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err : new Error('Failed to create consultation booking'),
      };
    }
  },

  async getBookingsByEmail(email: string): Promise<{ data: ConsultationBooking[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('consultation_bookings')
        .select('*')
        .eq('email', email.toLowerCase().trim())
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase
      const bookings: ConsultationBooking[] = (data || []).map((row: BookingRow) => ({
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone || undefined,
        companyName: row.company_name || undefined,
        message: row.message || undefined,
        bookingType: row.booking_type,
        createdAt: row.created_at,
        status: row.status,
      }));

      return { data: bookings, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err : new Error('Failed to fetch bookings'),
      };
    }
  },
};